import React, { useMemo, useState } from "react";
import { Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaShieldAlt } from "react-icons/fa";
import DashMedidasSectionCard from "../../DashMedidas/components/DashMedidasSectionCard/DashMedidasSectionCard";
import { buildDataQualityReport } from "../utils/dataInsights";

const STATUS_META = {
  empty: {
    label: "Sem dados",
    className: "relatorios-quality__status--empty",
  },
  good: {
    label: "Boa cobertura",
    className: "relatorios-quality__status--good",
  },
  warning: {
    label: "Atenção",
    className: "relatorios-quality__status--warning",
  },
  critical: {
    label: "Baixa cobertura",
    className: "relatorios-quality__status--critical",
  },
};

const DataQualityCard = ({
  allData = [],
  equipmentOptions = [],
  hasEquipmentSelection = false,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const report = useMemo(
    () =>
      buildDataQualityReport({
        allData,
        equipmentOptions,
      }),
    [allData, equipmentOptions]
  );

  if (!hasEquipmentSelection) {
    return null;
  }

  const status = STATUS_META[report.status] || STATUS_META.empty;
  const headerRight = (
    <span className="dash-section-card__header-meta">
      {report.coveragePercent}% cobertura
    </span>
  );

  return (
    <DashMedidasSectionCard
      icon={FaShieldAlt}
      subtitle="Indicador"
      title="Qualidade dos Dados"
      headerRight={headerRight}
      collapsible
      sectionId="relatorios-qualidade"
      className="relatorios-quality-card"
    >
      <div className="relatorios-quality">
        <div className="relatorios-quality__summary">
          <div className={`relatorios-quality__status ${status.className}`}>
            <strong>{status.label}</strong>
            <span>
              {report.withDataCount}/{report.selectedCount || 0} equipamentos
              com medição
            </span>
          </div>

          <div className="relatorios-quality__metrics">
            <div className="relatorios-quality__metric">
              <span className="relatorios-quality__metric-label">Cobertura</span>
              <strong>{report.coveragePercent}%</strong>
            </div>
            <div className="relatorios-quality__metric">
              <span className="relatorios-quality__metric-label">Medições</span>
              <strong>{report.totalMeasurements}</strong>
            </div>
            <div className="relatorios-quality__metric">
              <span className="relatorios-quality__metric-label">Gaps</span>
              <strong>{report.gapCount}</strong>
            </div>
            <div className="relatorios-quality__metric">
              <span className="relatorios-quality__metric-label">Intervalo típico</span>
              <strong>
                {report.medianIntervalMinutes != null
                  ? `${report.medianIntervalMinutes} min`
                  : "--"}
              </strong>
            </div>
          </div>
        </div>

        <div className="relatorios-quality__bar" aria-hidden>
          <div
            className={`relatorios-quality__bar-fill ${status.className}`}
            style={{ width: `${Math.min(100, report.coveragePercent)}%` }}
          />
        </div>

        <div className="relatorios-quality__meta">
          <span>
            Período nos dados: {report.dateRange.start} — {report.dateRange.end}
          </span>
          {report.gapThresholdHours != null && report.medianIntervalMinutes != null ? (
            <span>
              Gap considerado a partir de ~{report.gapThresholdHours} h sem medição
            </span>
          ) : null}
        </div>

        {!report.hasEquipmentIdentity && report.totalMeasurements > 0 ? (
          <p className="relatorios-quality__hint">
            As medições retornadas não incluem serial do equipamento. A cobertura
            por equipamento fica limitada — o ideal é o backend enviar
            <code> serial</code> ou <code> serialnumber</code> em cada registro.
          </p>
        ) : null}

        {(report.withoutDataTotal > 0 || report.gapCount > 0) && (
          <button
            type="button"
            className="relatorios-quality__toggle"
            onClick={() => setShowDetails((prev) => !prev)}
          >
            {showDetails ? "Ocultar detalhes" : "Ver equipamentos e gaps"}
          </button>
        )}

        {showDetails && (
          <div className="relatorios-quality__details">
            {report.withoutDataTotal > 0 && (
              <div className="relatorios-quality__block">
                <h6>
                  Sem medição no período
                  <Badge bg="warning" text="dark" pill>
                    {report.withoutDataTotal}
                  </Badge>
                </h6>
                <ul>
                  {report.withoutData.map((item) => (
                    <li key={item.serial}>
                      <strong>{item.label}</strong>
                      {item.unitName ? ` · ${item.unitName}` : ""}
                      <span className="relatorios-quality__serial">{item.serial}</span>
                    </li>
                  ))}
                  {report.withoutDataTotal > report.withoutData.length && (
                    <li className="text-secondary">
                      +{report.withoutDataTotal - report.withoutData.length} outros
                    </li>
                  )}
                </ul>
              </div>
            )}

            {report.gapCount > 0 && (
              <div className="relatorios-quality__block">
                <h6>
                  Maiores intervalos sem dados
                  <Badge bg="secondary" pill>
                    {report.gapCount}
                  </Badge>
                </h6>
                <ul>
                  {report.gaps.map((gap) => (
                    <li key={`${gap.serial}-${gap.startLabel}`}>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            {gap.startLabel} → {gap.endLabel}
                          </Tooltip>
                        }
                      >
                        <span>
                          <strong>{gap.label}</strong>
                          <span className="relatorios-quality__gap-hours">
                            {gap.hoursLabel}
                          </span>
                          <span className="relatorios-quality__serial">
                            {gap.startLabel} → {gap.endLabel}
                          </span>
                        </span>
                      </OverlayTrigger>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </DashMedidasSectionCard>
  );
};

export default DataQualityCard;
